import { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { useSlotTheme } from '../../hooks/useSlotTheme';
import { Reel, type ReelState } from './Reel';
import type { SymbolId } from '../../themes/types';
import type { ResolvedReelDivider, ResolvedSlotSkin } from '../../themes/skin-types';
import * as THREE from 'three';

// =============================================================================
// Types
// =============================================================================

export interface ReelContainerProps {
  /** Current spin state */
  isSpinning: boolean;
  /** Grid results from server (5 columns x 3 rows) */
  gridResult: SymbolId[][] | null;
  /** Winning positions per reel [{col, row}...] */
  winningPositions?: Array<{ col: number; row: number }>;
  /** Callback when all reels have stopped */
  onAllReelsStopped?: () => void;
  /** Callback when a specific reel stops */
  onReelStopped?: (reelIndex: number) => void;
  /** Optional dynamic skin for asset loading */
  skin?: ResolvedSlotSkin | null;
}

// =============================================================================
// Constants
// =============================================================================

const REEL_SPACING = 1.2; // Space between reels
const REEL_COUNT = 5;

// =============================================================================
// Reel Container Component
// =============================================================================

export function ReelContainer({
  isSpinning,
  gridResult,
  winningPositions = [],
  onAllReelsStopped,
  onReelStopped,
  skin,
}: ReelContainerProps) {
  const { theme } = useSlotTheme();

  // Track state of each reel
  const [reelStates, setReelStates] = useState<ReelState[]>(
    Array(REEL_COUNT).fill('idle')
  );

  // Track how many reels have stopped (using ref to avoid re-renders)
  const stoppedCountRef = useRef(0);

  // Track if we're currently in a spin cycle (started spinning, waiting for result)
  const isSpinCycleActiveRef = useRef(false);

  // Track the previous gridResult to detect changes
  const prevGridResultRef = useRef<SymbolId[][] | null>(null);

  // Generate default/random symbols for display before first spin
  const [displayGrid, setDisplayGrid] = useState<SymbolId[][]>(() =>
    generateRandomGrid(theme.reelStrip, REEL_COUNT, 3)
  );

  // Handle spin start
  useEffect(() => {
    if (isSpinning && !isSpinCycleActiveRef.current) {
      // Mark spin cycle as active
      isSpinCycleActiveRef.current = true;

      // Reset stopped count
      stoppedCountRef.current = 0;

      // Start all reels spinning with staggered delays
      const startDelays = [0, 100, 200, 300, 400];

      startDelays.forEach((delay, index) => {
        setTimeout(() => {
          setReelStates((prev) => {
            const next = [...prev];
            next[index] = 'spinning';
            return next;
          });
        }, delay);
      });
    }
  }, [isSpinning]);

  // Handle spin stop (when gridResult changes during an active spin cycle)
  useEffect(() => {
    // Only trigger stop if:
    // 1. We have a new grid result (different from previous)
    // 2. We're in an active spin cycle
    const hasNewResult = gridResult && gridResult !== prevGridResultRef.current;

    if (hasNewResult && isSpinCycleActiveRef.current) {
      // Update refs
      prevGridResultRef.current = gridResult;

      // Update display grid
      setDisplayGrid(gridResult);

      // Stop reels with staggered delays
      // Use shorter base delay since we want reels to start stopping soon after result arrives
      const stopDelay = theme.animations.reelStopDelay;
      const baseDelay = 500; // Start stopping 500ms after result arrives

      for (let i = 0; i < REEL_COUNT; i++) {
        setTimeout(() => {
          setReelStates((prev) => {
            const next = [...prev];
            next[i] = 'stopping';
            return next;
          });
        }, baseDelay + i * stopDelay);
      }

      // Mark spin cycle as complete (will be reset on next spin)
      isSpinCycleActiveRef.current = false;
    }
  }, [gridResult, theme.animations]);

  // Handle individual reel stopped
  const handleReelStopped = useCallback(
    (reelIndex: number) => {
      setReelStates((prev) => {
        const next = [...prev];
        next[reelIndex] = 'stopped';
        return next;
      });

      stoppedCountRef.current += 1;

      // Notify parent
      onReelStopped?.(reelIndex);

      // Check if all reels stopped
      if (stoppedCountRef.current === REEL_COUNT) {
        setTimeout(() => {
          onAllReelsStopped?.();
        }, 100);
      }
    },
    [onReelStopped, onAllReelsStopped]
  );

  // Get winning positions for a specific reel
  const getWinningPositionsForReel = useCallback(
    (reelIndex: number): number[] => {
      return winningPositions
        .filter((pos) => pos.col === reelIndex)
        .map((pos) => pos.row);
    },
    [winningPositions]
  );

  // Calculate X offset to center the reels
  const totalWidth = (REEL_COUNT - 1) * REEL_SPACING;
  const startX = -totalWidth / 2;

  return (
    <group>
      {/* Background panel for reels */}
      <mesh position={[0, 0, -0.5]}>
        <boxGeometry args={[totalWidth + 1.5, 3.5, 0.2]} />
        <meshStandardMaterial
          color={theme.style.reelBackground}
          metalness={0.1}
          roughness={0.9}
        />
      </mesh>

      {/* Render each reel */}
      {Array.from({ length: REEL_COUNT }).map((_, index) => (
        <Reel
          key={`reel-${index}`}
          index={index}
          xPosition={startX + index * REEL_SPACING}
          state={reelStates[index]}
          finalSymbols={displayGrid[index] ?? ['letter_r', 'letter_u', 'letter_m']}
          winningPositions={getWinningPositionsForReel(index)}
          onStopped={() => handleReelStopped(index)}
          speedMultiplier={1 + index * 0.1} // Slight speed variation per reel
        />
      ))}

      {/* Vertical dividers between reels */}
      <ReelDividers
        startX={startX}
        reelSpacing={REEL_SPACING}
        reelCount={REEL_COUNT}
        dividerAsset={skin?.reelDivider}
      />

      {/* Outer frame */}
      <OuterFrame totalWidth={totalWidth + 1.5} />

      {/* Payline indicator (center line) */}
      <PaylineIndicator width={totalWidth + 1} />
    </group>
  );
}

// =============================================================================
// Reel Dividers (vertical separators between reels)
// =============================================================================

interface ReelDividersProps {
  startX: number;
  reelSpacing: number;
  reelCount: number;
  /** Optional dynamic divider asset */
  dividerAsset?: ResolvedReelDivider | null;
}

function ReelDividers({ startX, reelSpacing, reelCount, dividerAsset }: ReelDividersProps) {
  const dividerHeight = 3.4; // Match frame height

  // Load texture if divider is an image
  const texture = useMemo(() => {
    if (!dividerAsset || dividerAsset.type !== 'image') return null;

    const loader = new THREE.TextureLoader();
    const tex = loader.load(dividerAsset.url);
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    return tex;
  }, [dividerAsset]);

  return (
    <group>
      {/* Dividers between reels (reelCount - 1 dividers) */}
      {Array.from({ length: reelCount - 1 }).map((_, i) => {
        // Position between reel i and reel i+1
        // Reel i is at startX + i * reelSpacing
        // Reel i+1 is at startX + (i+1) * reelSpacing
        // Midpoint = startX + (i + 0.5) * reelSpacing
        const xPos = startX + (i + 0.5) * reelSpacing;

        // If we have a divider image, render it as a textured plane
        if (texture) {
          const scale = dividerAsset?.scale ?? 1;
          return (
            <mesh key={`divider-${i}`} position={[xPos, 0, 0.2]}>
              <planeGeometry args={[0.15 * scale, dividerHeight * scale]} />
              <meshBasicMaterial map={texture} transparent side={THREE.DoubleSide} />
            </mesh>
          );
        }

        // Default: render as a 3D box
        return (
          <mesh key={`divider-${i}`} position={[xPos, 0, 0.2]}>
            <boxGeometry args={[0.1, dividerHeight, 0.15]} />
            <meshStandardMaterial
              color="#3d5a7f" // Visible blue-gray for dividers
              metalness={0.5}
              roughness={0.5}
            />
          </mesh>
        );
      })}
    </group>
  );
}

// =============================================================================
// Outer Frame
// =============================================================================

interface OuterFrameProps {
  totalWidth: number;
}

function OuterFrame({ totalWidth }: OuterFrameProps) {
  const { theme } = useSlotTheme();
  const frameHeight = 3.5;
  const frameThickness = 0.12;
  const halfWidth = totalWidth / 2;

  return (
    <group>
      {/* Left border */}
      <mesh position={[-halfWidth - frameThickness / 2, 0, 0.05]}>
        <boxGeometry args={[frameThickness, frameHeight, 0.1]} />
        <meshStandardMaterial
          color={theme.style.secondary}
          metalness={0.8}
          roughness={0.2}
        />
      </mesh>

      {/* Right border */}
      <mesh position={[halfWidth + frameThickness / 2, 0, 0.05]}>
        <boxGeometry args={[frameThickness, frameHeight, 0.1]} />
        <meshStandardMaterial
          color={theme.style.secondary}
          metalness={0.8}
          roughness={0.2}
        />
      </mesh>

      {/* Top border */}
      <mesh position={[0, frameHeight / 2 + frameThickness / 2, 0.05]}>
        <boxGeometry args={[totalWidth + frameThickness * 2, frameThickness, 0.1]} />
        <meshStandardMaterial
          color={theme.style.secondary}
          metalness={0.8}
          roughness={0.2}
        />
      </mesh>

      {/* Bottom border */}
      <mesh position={[0, -frameHeight / 2 - frameThickness / 2, 0.05]}>
        <boxGeometry args={[totalWidth + frameThickness * 2, frameThickness, 0.1]} />
        <meshStandardMaterial
          color={theme.style.secondary}
          metalness={0.8}
          roughness={0.2}
        />
      </mesh>
    </group>
  );
}

// =============================================================================
// Payline Indicator
// =============================================================================

interface PaylineIndicatorProps {
  width: number;
}

function PaylineIndicator({ width }: PaylineIndicatorProps) {
  const { theme } = useSlotTheme();

  return (
    <group position={[0, 0, 0.2]}>
      {/* Center payline marker */}
      <mesh position={[-width / 2 - 0.1, 0, 0]}>
        <coneGeometry args={[0.1, 0.2, 3]} />
        <meshStandardMaterial color={theme.style.primary} />
      </mesh>
      <mesh position={[width / 2 + 0.1, 0, 0]} rotation={[0, 0, Math.PI]}>
        <coneGeometry args={[0.1, 0.2, 3]} />
        <meshStandardMaterial color={theme.style.primary} />
      </mesh>
    </group>
  );
}

// =============================================================================
// Helpers
// =============================================================================

function generateRandomGrid(
  reelStrip: SymbolId[],
  columns: number,
  rows: number
): SymbolId[][] {
  const grid: SymbolId[][] = [];

  for (let col = 0; col < columns; col++) {
    const column: SymbolId[] = [];
    for (let row = 0; row < rows; row++) {
      const randomIndex = Math.floor(Math.random() * reelStrip.length);
      column.push(reelStrip[randomIndex]);
    }
    grid.push(column);
  }

  return grid;
}

export default ReelContainer;
